/**
 * Chrome copy and URLs. Prefer FRONTEND_SITE_CONFIG.commonAppConfig.BSG_CHROME
 * (plugin). Fall back to defaults.js (synced from settings.json) offline.
 */
import { getAuthenticatedHttpClient, getAuthenticatedUser, getSiteConfig } from "@openedx/frontend-base";
import defaults from "./defaults.js";
import { helpFormUrl, rewriteHelpChrome } from "./helpUrl.js";
import { isAuthoringPath } from "./isLearnerChrome.js";
import {
  FALLBACK_FOOTER_MARK,
  FALLBACK_LEARN_LOGO,
  FALLBACK_SITE_LOGO,
} from "./logos.js";

/**
 * Leftover MFEs live on apps.learn… ; `/static/indigo/…` there is HTML 200,
 * not a PNG (L-029). Prefix root-relative paths with LMS_BASE_URL. Relative
 * `../assets/…` strings are not webpack URLs — use the bundled data URI.
 */
export function resolveAssetUrl(url, lmsBaseUrl, fallback) {
  const value = String(url || "").trim();
  const lms = String(lmsBaseUrl || "").replace(/\/$/, "");
  if (/^data:/i.test(value) || /^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/") && lms) return `${lms}${value}`;
  return fallback || value;
}

/** LMS routes on leftover hosts (apps.learn…) must not stay root-relative. */
export function resolveLmsUrl(url, lmsBaseUrl) {
  const value = String(url || "").trim();
  const lms = String(lmsBaseUrl || "").replace(/\/$/, "");
  if (!value || /^https?:\/\//i.test(value) || /^mailto:/i.test(value) || value.startsWith("#")) {
    return value;
  }
  if (value.startsWith("/") && lms) return `${lms}${value}`;
  return value;
}

export function readUser() {
  try {
    const user = getAuthenticatedUser();
    if (!user) return { signedIn: false, name: "", email: "", username: "", avatarUrl: "" };
    return {
      signedIn: true,
      name: user.name || user.username || "",
      email: user.email || "",
      username: user.username || "",
      avatarUrl: "",
    };
  } catch {
    return {
      signedIn: true,
      name: defaults.fixtureName || "Fixture Person",
      email: defaults.fixtureEmail || "fixture@example.org",
      username: "fixture",
      avatarUrl: "",
    };
  }
}

export function readChrome() {
  let site = {};
  let fromSite = {};
  try {
    site = getSiteConfig() || {};
    fromSite = site.commonAppConfig?.BSG_CHROME || {};
  } catch {
    fromSite = {};
  }
  const chrome = { ...defaults, ...fromSite };
  const lms = site.lmsBaseUrl || chrome.lmsBaseUrl || chrome.learnUrl || "";
  const logout = site.logoutUrl || chrome.logoutUrl || "/logout";
  // L-036: force Help onto the LMS form even when saved BSG_CHROME still
  // has /support (L-033) in sub-brand / footer Learn. helpUrl alone is
  // not what markup.js renders.
  rewriteHelpChrome(chrome, helpFormUrl(lms, chrome.learnUrl));
  chrome.logoutUrl = resolveLmsUrl(logout, lms) || logout;
  chrome.loginUrl = site.loginUrl || chrome.loginUrl;
  chrome.lmsBaseUrl = lms;
  chrome.coursesUrl = resolveLmsUrl(chrome.coursesUrl, lms);
  chrome.dashboardUrl = resolveLmsUrl(chrome.dashboardUrl, lms);
  chrome.helpUrl = resolveLmsUrl(chrome.helpUrl, lms);
  chrome.siteLogoUrl = resolveAssetUrl(chrome.siteLogoUrl, lms, FALLBACK_SITE_LOGO);
  chrome.learnLogoUrl = resolveAssetUrl(chrome.learnLogoUrl, lms, FALLBACK_LEARN_LOGO);
  chrome.footerMarkUrl = resolveAssetUrl(chrome.footerMarkUrl, lms, FALLBACK_FOOTER_MARK);
  if (Array.isArray(chrome.subbrandItems)) {
    chrome.subbrandItems = chrome.subbrandItems.map((item) => ({
      ...item,
      href: resolveLmsUrl(item.href, lms),
    }));
  }
  if (Array.isArray(chrome.footerColumns)) {
    chrome.footerColumns = chrome.footerColumns.map((col) => ({
      ...col,
      links: (col.links || []).map((link) => ({
        ...link,
        url: resolveLmsUrl(link.url, lms),
      })),
    }));
  }
  chrome.user = readUser();
  // L-045: authoring folds Studio account + actions into this chrome.
  // Other leftover MFEs stay on the catalog account rows.
  try {
    chrome.studioUnifiedNav = isAuthoringPath();
  } catch {
    chrome.studioUnifiedNav = Boolean(chrome.studioUnifiedNav);
  }
  if (chrome.studioUnifiedNav) {
    chrome.studioHomeUrl = chrome.studioHomeUrl || "/authoring/home";
    chrome.studioHomeLabel = chrome.studioHomeLabel || "Studio";
    chrome.studioHomeNavLabel = chrome.studioHomeNavLabel || "Studio Home";
    chrome.studioLibrariesUrl = chrome.studioLibrariesUrl || "/authoring/libraries";
    chrome.studioLibrariesLabel = chrome.studioLibrariesLabel || "Libraries";
    chrome.studioSearchUrl = chrome.studioSearchUrl || chrome.studioHomeUrl;
    chrome.studioNotificationsLabel = chrome.studioNotificationsLabel || "Notifications";
    chrome.studioSearchLabel = chrome.studioSearchLabel || "Search";
    // L-055: Studio white sub-bar is Studio Home / Libraries / LMS
    // dashboard. LMS Courses / Dashboard / Help stay on learner pages.
    chrome.studioSubbrandItems = Array.isArray(chrome.studioSubbrandItems)
      && chrome.studioSubbrandItems.length
      ? chrome.studioSubbrandItems.map((item) => ({
        ...item,
        href: /^https?:\/\//i.test(item.href) || String(item.href || "").startsWith("/authoring")
          ? item.href
          : resolveLmsUrl(item.href, lms),
      }))
      : [
        { label: chrome.studioHomeNavLabel, href: chrome.studioHomeUrl, key: "studio-home" },
        { label: chrome.studioLibrariesLabel, href: chrome.studioLibrariesUrl, key: "libraries" },
        { label: chrome.learnDashboardLabel || "BSG Learn", href: chrome.dashboardUrl, key: "learn-dashboard" },
      ];
  }
  return chrome;
}

export async function fetchAvatar(chrome) {
  const username = chrome.user?.username;
  const base = chrome.lmsBaseUrl;
  if (!username || !base) return "";
  try {
    const client = getAuthenticatedHttpClient();
    const response = await client.get(`${base.replace(/\/$/, "")}/api/user/v1/accounts/${username}`);
    const image = response?.data?.profile_image;
    if (image?.has_image) return image.image_url_medium || "";
  } catch {
    return "";
  }
  return "";
}